import React, { useMemo } from 'react'

import type { CheckboxProps } from './types'
import type {
  UseCheckboxGroupProps,
  UseCheckboxGroupReturn
} from './use-checkbox-group'
import { useCheckboxGroup } from './use-checkbox-group'
import { __DEV__ } from '../../utils/assertion'
import { createContext } from '../../utils/react-helpers'

export type CheckboxGroupProps = {
  children?: React.ReactNode
} & UseCheckboxGroupProps &
  Pick<CheckboxProps, 'size' | 'variant' | 'state'>

export type CheckboxGroupContext = {} & Pick<
  UseCheckboxGroupReturn,
  'onChange' | 'value' | 'disabled'
>

const [CheckboxGroupProvider, useCheckboxGroupContext] =
  createContext<CheckboxGroupContext>({
    name: 'CheckboxGroupContext',
    strict: false
  })

export { useCheckboxGroupContext }

/**
 * Used for multiple checkboxes which are bound in one group,
 * and it indicates whether one or more options are selected.
 *
 */
const CheckboxGroup: React.FC<CheckboxGroupProps> = props => {
  const { state, size, variant, children, disabled } = props
  const { value, onChange } = useCheckboxGroup(props)

  const group = useMemo(
    () => ({
      size,
      onChange,
      state,
      value,
      variant,
      disabled
    }),
    [state, size, onChange, value, variant, disabled]
  )

  return <CheckboxGroupProvider value={group}>{children}</CheckboxGroupProvider>
}

if (__DEV__) {
  CheckboxGroup.displayName = 'CheckboxGroup'
}

export default CheckboxGroup
